import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import { notifications } from '../../utils/toast';
import { formatLocalDate } from '../../utils/dateUtils';

/**
 * Certificate Viewer Page
 * Full-page professional certificate view with download/print
 */

const CertificateViewerPage = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const certificateRef = useRef(null);
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [participant, setParticipant] = useState(null);

  useEffect(() => {
    // Get participant from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setParticipant(JSON.parse(userData));
    }

    // Simulate loading certificate data
    setTimeout(() => {
      // Mock certificate data - in real app, fetch by certificateId
      setCertificate({
        id: certificateId,
        sessionId: 'session-1',
        sessionName: 'Leadership Training Workshop',
        organizationName: 'TechCorp Inc.',
        organizationLogo: null,
        trainerName: 'Sarah Johnson',
        trainerSignature: 'Sarah Johnson',
        completionDate: '2024-01-15',
        issueDate: '2024-01-16',
        score: 92,
        duration: '8 hours',
        certificateNumber: 'CERT-2024-001234',
        skills: ['Leadership', 'Team Management', 'Communication', 'Strategic Planning'],
        description: 'This certifies that the participant has successfully completed the Leadership Training Workshop and demonstrated exceptional understanding of leadership principles and practices.',
      });
      setLoading(false);
    }, 500);
  }, [certificateId]);

  const handleDownload = () => {
    // TODO: Implement PDF generation using jsPDF or similar
    notifications.success('Certificate download started!');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    // TODO: Implement share functionality
    notifications.info('Share functionality coming soon!');
  };

  if (loading) {
    return (
      <DashboardLayout title="Loading Certificate...">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-neutral-600">Loading certificate...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!certificate) {
    return (
      <DashboardLayout title="Certificate Not Found">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Certificate Not Found</h2>
            <Button onClick={() => navigate('/participant/certificates')}>
              Back to Certificates
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      {/* Action Bar - Hidden on Print */}
      <div className="print:hidden sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/participant/certificates')}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Certificates
          </Button>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleShare}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </svg>
              Share
            </Button>
            <Button variant="outline" onClick={handlePrint}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Print
            </Button>
            <Button variant="primary" onClick={handleDownload}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Certificate Container */}
      <div className="bg-neutral-100 min-h-screen py-12 print:py-0 print:bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-5xl mx-auto"
        >
          {/* Certificate */}
          <div
            ref={certificateRef}
            className="bg-white shadow-2xl print:shadow-none mx-4 print:mx-0"
            style={{ aspectRatio: '11/8.5' }}
          >
            {/* Decorative Border */}
            <div className="p-8 h-full border-8 border-double border-amber-400">
              <div className="h-full border-2 border-amber-300 p-12 flex flex-col">
                {/* Header */}
                <div className="text-center mb-8">
                  {/* Logo Placeholder */}
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-600 rounded-full mb-6">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <h1 className="text-5xl font-serif font-bold text-neutral-900 mb-2">
                    Certificate of Completion
                  </h1>
                  <div className="w-32 h-1 bg-amber-400 mx-auto"></div>
                </div>

                {/* Body */}
                <div className="flex-1 flex flex-col justify-center text-center space-y-6">
                  <p className="text-lg text-neutral-700">This is to certify that</p>

                  <h2 className="text-4xl font-serif font-bold text-primary-900 border-b-2 border-neutral-300 pb-3 mx-16">
                    {participant?.name || 'Participant Name'}
                  </h2>

                  <p className="text-lg text-neutral-700">
                    has successfully completed the training program
                  </p>

                  <h3 className="text-3xl font-serif font-semibold text-neutral-900">
                    {certificate.sessionName}
                  </h3>

                  <p className="text-neutral-600 max-w-2xl mx-auto leading-relaxed">
                    {certificate.description}
                  </p>

                  {/* Achievement Score */}
                  <div className="flex items-center justify-center gap-8 pt-4">
                    <div>
                      <p className="text-sm text-neutral-500 mb-1">Achievement Score</p>
                      <p className="text-3xl font-bold text-amber-600">{certificate.score}%</p>
                    </div>
                    <div className="w-px h-12 bg-neutral-300"></div>
                    <div>
                      <p className="text-sm text-neutral-500 mb-1">Duration</p>
                      <p className="text-2xl font-semibold text-neutral-700">{certificate.duration}</p>
                    </div>
                  </div>

                  {/* Skills */}
                  <div className="pt-4">
                    <p className="text-sm text-neutral-500 mb-3">Skills Acquired</p>
                    <div className="flex flex-wrap justify-center gap-3">
                      {certificate.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-4 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium border border-primary-200"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-8 pt-8 border-t border-neutral-300">
                  <div className="grid grid-cols-3 gap-8 text-center">
                    {/* Date */}
                    <div>
                      <div className="border-t-2 border-neutral-400 pt-2 mx-8">
                        <p className="font-semibold text-neutral-900">
                          {new Date(certificate.issueDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                        <p className="text-sm text-neutral-500 mt-1">Date Issued</p>
                      </div>
                    </div>

                    {/* Trainer Signature */}
                    <div>
                      <div className="border-t-2 border-neutral-400 pt-2 mx-8">
                        <p className="font-signature text-2xl text-neutral-900 mb-1">
                          {certificate.trainerSignature}
                        </p>
                        <p className="text-sm font-semibold text-neutral-700">{certificate.trainerName}</p>
                        <p className="text-xs text-neutral-500 mt-1">Certified Trainer</p>
                      </div>
                    </div>

                    {/* Organization */}
                    <div>
                      <div className="border-t-2 border-neutral-400 pt-2 mx-8">
                        <p className="font-semibold text-neutral-900">{certificate.organizationName}</p>
                        <p className="text-sm text-neutral-500 mt-1">Training Organization</p>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Number */}
                  <div className="text-center mt-6">
                    <p className="text-xs text-neutral-400">
                      Certificate No: <span className="font-mono">{certificate.certificateNumber}</span>
                    </p>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute top-12 left-12 w-16 h-16 border-t-4 border-l-4 border-amber-400 opacity-50"></div>
                <div className="absolute top-12 right-12 w-16 h-16 border-t-4 border-r-4 border-amber-400 opacity-50"></div>
                <div className="absolute bottom-12 left-12 w-16 h-16 border-b-4 border-l-4 border-amber-400 opacity-50"></div>
                <div className="absolute bottom-12 right-12 w-16 h-16 border-b-4 border-r-4 border-amber-400 opacity-50"></div>
              </div>
            </div>
          </div>

          {/* Certificate Info - Hidden on Print */}
          <div className="print:hidden mt-8 mx-4">
            <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Certificate Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-500">Session</p>
                  <p className="font-medium text-neutral-900">{certificate.sessionName}</p>
                </div>
                <div>
                  <p className="text-neutral-500">Organization</p>
                  <p className="font-medium text-neutral-900">{certificate.organizationName}</p>
                </div>
                <div>
                  <p className="text-neutral-500">Completion Date</p>
                  <p className="font-medium text-neutral-900">
                    {formatLocalDate(certificate.completionDate)}
                  </p>
                </div>
                <div>
                  <p className="text-neutral-500">Issue Date</p>
                  <p className="font-medium text-neutral-900">
                    {formatLocalDate(certificate.issueDate)}
                  </p>
                </div>
                <div>
                  <p className="text-neutral-500">Trainer</p>
                  <p className="font-medium text-neutral-900">{certificate.trainerName}</p>
                </div>
                <div>
                  <p className="text-neutral-500">Certificate Number</p>
                  <p className="font-mono text-xs text-neutral-700">{certificate.certificateNumber}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          @page {
            size: landscape;
            margin: 0.5in;
          }
        }
        .font-signature {
          font-family: 'Brush Script MT', cursive;
        }
      `}</style>
    </>
  );
};

export default CertificateViewerPage;
